import { create } from 'zustand';

import type {
  EnrolmentOutcome,
  EnrolmentProbeOutcome,
  EnrolmentTokenOutcome,
  ProbeTrigger,
} from '@/services/enrolment';
import { enrolDevice, issueEnrolmentToken, nameKiosk, probeEnrolment } from '@/services/enrolment';
import { onUnauthenticated } from '@/services/unauthenticated';

import { useKioskSession } from './kioskSession';

/**
 * The DEVICE's enrolment state — six digits, four server verdicts, the fork after them, and the naming step
 * the published branch lands on [03-us-kad-6c, 03-us-kad-6d; decisions 0203, 0204, 0221, 0245].
 *
 * ⭐ **The name lives here because it hangs off the KIOSK SESSION** [[`0272`]] — ⛔ not the candidate
 * session and not a device-scoped record — so it shares this store's lifetime exactly: it survives every
 * candidate who uses the tablet and it is gone when the device is back at the pad.
 *
 * It is a store of its own, beside `kioskSession` rather than inside it, because the two have opposite
 * lifetimes. Everything in `kioskSession`'s candidate slice is wiped when somebody walks away — that is the
 * whole point of `endCandidateSession` — and an enrolment must survive every candidate who uses the tablet.
 *
 * ## The invariant this file exists to hold
 *
 * ⭐ **The tablet renders the server's verdict and never computes one** [KAD-6c § *The invariant*].
 *
 * Which screen appears is a function of the last response and nothing else. ⛔ There is no attempt counter
 * here, no lock flag, no retry budget and no timer: `refusal` holds what the server SAID, it is replaced by
 * the next answer, and nothing on this device ever advances it. The three-strike lock is the server's, keyed
 * to the layer-1 token — a client-side count would be a bug even on the runs where it happened to agree.
 *
 * ⛔ **And nothing is written to the device.** No `localStorage`, no `sessionStorage`, no script-readable
 * cookie [AC-11]: the layer-1 token and the session credential are both `HttpOnly` cookies this app never
 * reads [0221].
 *
 * ⚠️ **Holding this in memory used to mean a tablet that RELOADED came back to the PIN pad** — the app
 * could not ask the server whether it was still enrolled, because the credential is a cookie it cannot read
 * and the api published no route that answered the question [decision 0260]. ⭐ **[03-us-kad-6i] closes
 * that**: `GET /enrolment/me` is the route [decision 0280], every load asks it
 * [{@link EnrolmentSessionState.checkEnrolment}], and the answer is the server's — ⛔ **still nothing is
 * remembered on the device.** The enrolment itself always survived [0170; 0241]; what is no longer spent is
 * the staffer's time.
 */
export const ENROLMENT = {
  /**
   * ⭐ **The state every load COMMITS FIRST** — the boot probe is with the server and nothing has been
   * claimed yet [03-us-kad-6i AC-11].
   *
   * ⛔ **Not `pin`.** The pad may not render at any point on an enrolled tablet's reload, not for one frame,
   * and the pad's own `useEffect` is what calls `GET /enrolment/session` — a route that mints a Redis key
   * per call.
   *
   * ⛔ **It is undrawn, and that is a RULING rather than a gap** [decision `0307`, Operator 2026-09-18]: the
   * app's background colour carrying ONE drawn element, the app's existing `Spinner` — ⛔ no frame, no card,
   * no logo, no heading, no copy, no back or exit control. ⭐ **The wait is ANNOUNCED separately**
   * [`EnrolmentAnnouncement`], because `Spinner` is `role="presentation"` and says nothing at all to a
   * screen reader.
   *
   * ⭐ **It is re-entered by the ONE bounded recovery** [AC-10] and by nothing else — never on a timer.
   */
  checking: 'checking',
  /** Frames 01–04: the pad, with or without the server's last refusal beside it. */
  pin: 'pin',
  /** Frame 06 · Connecting. Six digits are with the server and the staffer waits [AC-7]. */
  connecting: 'connecting',
  /** Frame 05 · Locked. ⛔ No keypad, no retry, no timer — an admin is the only exit [AC-5]. */
  locked: 'locked',
  /**
   * Frames 07 · Name this kiosk and 08 · Name already taken — the fork's `published: true` branch, which
   * now lands HERE rather than on the candidate journey [03-us-kad-6d AC-1].
   */
  naming: 'naming',
  /** Frame 09 · Ready — named, bound, and one tap from the candidate journey [KAD-6d AC-5]. */
  ready: 'ready',
  /** ⭐ *Start* was tapped: the tablet is the candidate's now [KAD-6d AC-5; KID-1]. */
  enrolled: 'enrolled',
  /*
   * ⛔ **There is no *nothing published* state** [03-us-kad-6e AC-6; decision 0429 R1]. `published: false`
   * lands on the plain pad — {@link AT_THE_PAD} — at the boot and at the connect alike, so nothing could
   * enter a member of its own and it was removed rather than left for a later change to route into.
   */
} as const;
export type Enrolment = (typeof ENROLMENT)[keyof typeof ENROLMENT];

/**
 * What the server said about the last six digits, kept only to choose which drawn message renders.
 *
 * `incorrect-pin` carries the count the SERVER reported and is the only kind that carries anything at all.
 * Two of them have a frame; the other four are the paths the Operator ruled on 2026-09-16 — one inline
 * error in the treatment frames 03 and 04 already draw, saying as little as the state allows
 * [0261, 0262, 0263].
 */
export type PinRefusal =
  | { readonly kind: 'incorrect-pin'; readonly attemptsLeft: number | undefined }
  | { readonly kind: 'throttled' }
  | { readonly kind: 'rate-limited' }
  | { readonly kind: 'unreadable' }
  /**
   * ⭐ **Not an attempt at all** — the server refused the credential this device was already holding
   * [0245], so the app came back to the pad. It is a separate kind from `incorrect-pin` because nothing
   * was typed and nothing was counted; what it SAYS is the Operator's [0263], and the two rendering the
   * same sentence is a copy ruling rather than a shared meaning.
   */
  | { readonly kind: 'credential-refused' };

/**
 * What the server said about a typed NAME, kept only to choose which message the field renders.
 *
 * ⭐ **`taken` carries the name that was SUBMITTED**, not the field's current value: the staffer edits the
 * field to recover, and the sentence has to keep naming the name that was actually refused.
 *
 * ⛔ **`required` is not the server's** — it is the one refusal this device decides, because an empty field
 * is not a request worth making. Every OTHER verdict is the server's [§ *The invariant*: the client must not
 * pre-check a collision]. ⛔ **There is no `too-long` kind**: 40 characters is enforced where the value is
 * typed, so a 41st never reaches a submission [`0224`].
 */
export type NameRefusal =
  | { readonly kind: 'taken'; readonly name: string }
  | { readonly kind: 'required' }
  | { readonly kind: 'unreadable' };

/**
 * The bound on a kiosk name [[`0224`]] — ⛔ **40, never the `0/240` on the frame**, which is a component
 * slot default and is `visible: false` [[`0225`]].
 */
export const KIOSK_NAME_MAX_LENGTH = 40;

/** Whether this browser holds [0203]'s layer-1 token — the subject the three-strike lock counts against. */
export const TOKEN = { none: 'none', issued: 'issued' } as const;
export type Token = (typeof TOKEN)[keyof typeof TOKEN];

export const PIN_LENGTH = 6;

/**
 * What asked for the no-outcome state to be checked again [03-us-kad-6i AC-10].
 *
 * ⭐ **The distinction is the whole of the bounding.** A `'pressed'` recovery is one staffer, once, and it
 * is answered immediately; a `'signal'` is `online` or `visibilitychange`, which the environment delivers
 * to an entire branch at once and which therefore carries a floor and a jitter.
 */
export type RecoveryTrigger = Extract<ProbeTrigger, 'pressed' | 'signal'>;

/**
 * What made an ENROLLED tablet ask again whether its venue still serves [03-us-kad-6o]: a visit ending
 * (`reset`, [0433]) or the idle welcome's first tap (`tap`, [0430] § 1). ⛔ Nothing else asks — not a timer,
 * not the live socket, not any screen mid-visit [AC-4; 0354; 0430 § 2].
 */
export type VenueCheckTrigger = Extract<ProbeTrigger, 'reset' | 'tap'>;

/**
 * Who asked for the layer-1 token — ⭐ **the pad's mount, or six digits about to be submitted.**
 *
 * ⛔ **Only the mount is floored.** A submission's request is a person's, arrives at most once per six
 * digits, and refusing it would silently disarm [0203]'s layer 1 for a staffer standing at the tablet
 * [KAD-6c AC-1b].
 */
export type TokenRequest = 'mount' | 'submission';

export interface EnrolmentSessionState {
  readonly enrolment: Enrolment;
  /** The digits entered so far. Six of them submit; ⛔ they never reach web storage and never persist. */
  readonly digits: string;
  readonly refusal: PinRefusal | undefined;
  readonly token: Token;
  /**
   * Why the last token request failed, when the reason changes what the pad says.
   *
   * ⭐ **Only the coarse floor's 429 is carried**, because only it has its own ruled copy [`0262`]: the
   * floor guards the whole controller and counts `GET /enrolment/session` as a request, while layer 2 runs
   * inside the enrol handler and never sees this route. ⛔ **Without it the two controls would print
   * different sentences on a second attempt** — which is exactly what the Operator's *nothing disclosed*
   * ruling removes.
   */
  readonly tokenRefusal: 'rate-limited' | undefined;
  /**
   * The venue's display name, as the connect reported it — *Allentown, PA · DC 3* [KAD-6a § *Reference
   * shapes*].
   *
   * ⛔ **Not a venue id**, and there is none to hold: the api tells the kiosk which venue it is at so a
   * staffer can confirm it, and never gives it the key it would need to ask for that venue's data — the
   * venue travels on the credential [0204]. `undefined` when the answer did not carry one.
   */
  readonly venueName: string | undefined;
  /**
   * The name being typed, exactly as `digits` holds the PIN being typed: a draft that ⛔ never reaches web
   * storage and never persists [AC-11].
   *
   * It is in the store rather than in the screen for the same reason `digits` is — the design-fidelity gate
   * seeds a state and measures the render, and *08 · Name already taken* is a state with a value in the
   * field.
   */
  readonly name: string;
  /** The name the SERVER stored, echoed by the 200 — what *09 · Ready* names [AC-6]. */
  readonly kioskName: string | undefined;
  readonly nameRefusal: NameRefusal | undefined;
  /**
   * ⭐ **THIS LOAD's probe claimed no enrolment outcome** — a 503, a 429, a timeout, a body that is not the
   * contract [03-us-kad-6i AC-10].
   *
   * ⛔ **It is not a verdict and it answers nothing.** It is the absence of one, which is why it may live
   * here at all: the invariant forbids the device holding an attempt count, a lock flag, a retry budget or
   * a timer, and this is none of them — it is the single bit that says *nobody answered*, in memory only,
   * ⛔ never in web storage [AC-12].
   *
   * ⭐ **What it is FOR: the one bounded recovery** [{@link EnrolmentSessionState.recheckEnrolment}]. Without
   * it the no-outcome state is terminal, and a thirty-second store blip strands every tablet in the estate
   * until a human walks up to each one — with re-typing the PIN as the practical remedy, which is the exact
   * cost this story exists to remove.
   */
  readonly probeUnanswered: boolean;
  /**
   * Whether a naming request is open. ⛔ **Not a screen** — no frame draws a saving state — it is the
   * re-entry guard, and it greys the action for the moment the write is in flight so a slow branch network
   * does not read as a dead button [the defect `submit` records for the PIN pad].
   */
  readonly namingInFlight: boolean;
  /**
   * ⭐ **Asks the server whether this device is still enrolled, on THIS load** [03-us-kad-6i AC-7], and
   * renders the answer. ⛔ **One request in flight, ever** — a re-render cannot issue a second.
   */
  checkEnrolment: (trigger: ProbeTrigger) => Promise<void>;
  /**
   * ⭐ **The user-visible recovery out of the no-outcome state** [03-us-kad-6i AC-10] — ⛔ never a loop and
   * ⛔ never a timer. One act, one probe.
   *
   * ⭐ **`'pressed'` is a person and is never refused**; `'signal'` is the environment coming back and is
   * bounded by {@link RECOVERY_FLOOR_MS}, {@link PAD_IDLE_MS} and a jitter, because a signal arrives at
   * every tablet behind a branch address in the same instant.
   */
  recheckEnrolment: (trigger: RecoveryTrigger) => Promise<void>;
  /**
   * ⭐ **An enrolled tablet asks again whether its venue still serves — at every return to the welcome and
   * on the idle welcome's first tap, and never during a visit** [03-us-kad-6o § *The invariant*; 0433;
   * 0430]. One probe, single-flight with the boot's, ⛔ no timer.
   *
   * Resolves `true` only when the server said the venue serves (`published: true`) and the tablet is still
   * enrolled — the one answer on which the tap may start the journey [AC-2].
   */
  checkVenueServes: (trigger: VenueCheckTrigger) => Promise<boolean>;
  /** Fetches the layer-1 token, on the PIN screen's load and before any digit [AC-1b]. */
  issueToken: (reason: TokenRequest) => Promise<void>;
  pressDigit: (digit: string) => void;
  pressBackspace: () => void;
  /** Types into the name field, bounded at {@link KIOSK_NAME_MAX_LENGTH} [AC-2]. */
  typeName: (value: string) => void;
  /** Sends the typed name and renders the server's verdict [AC-1, AC-3]. */
  submitName: () => Promise<void>;
  /** *Start* on *09 · Ready* — the tablet becomes the candidate's [AC-5; KID-1]. */
  startTakingCandidates: () => void;
  /** [0245] — the server refused this device's credential, so it is an unenrolled device again. */
  refuseCredential: () => void;
}

/**
 * Every field that describes an enrolment, cleared — ⛔ **everything except WHICH state it is in.**
 *
 * ⭐ **The naming draft is part of it.** A device back at the pad is an unenrolled device, and the next
 * enrolment types its name again [0170; `0272` — the name hangs off the kiosk session, so a new session is
 * a new name]. Leaving a previous staffer's half-typed name in the field would be a value nobody entered.
 *
 * ⭐ **Exported because it is the ONE list, and a second hand-written copy drifts from it.** `enrolledDevice`
 * in `src/test/` had already lost `kioskName` and `venueName` from its own copy — in the helper whose whole
 * purpose is to stop a value leaking between suites. Anything that needs "a cleared enrolment in state X"
 * spreads this and overrides `enrolment`, so a field added here reaches every one of them.
 */
export const CLEARED_ENROLMENT = {
  digits: '',
  refusal: undefined,
  venueName: undefined,
  name: '',
  kioskName: undefined,
  nameRefusal: undefined,
  namingInFlight: false,
  /*
   * ⭐ **It belongs in this list, and leaving it out was the drift this constant exists to prevent.** The
   * helper that clears an enrolment for a suite had already lost two fields from its own hand-written copy
   * once [`src/test/enrolledDevice.ts`]; a flag that says *nobody answered on this load* surviving into a
   * seeded, enrolled device is the same bug with a worse ending — a candidate's journey replaced by a
   * spinner. ⛔ **Only {@link land}'s no-answer case sets it true**, explicitly, after this spread.
   */
  probeUnanswered: false,
} as const;

const AT_THE_PAD = { ...CLEARED_ENROLMENT, enrolment: ENROLMENT.pin } as const;

/**
 * ⭐ **The pad, for a device whose layer-1 token has been SPENT** — the connect's `published: false`, a
 * refused credential, and an enrolled tablet's re-check landing [03-us-kad-6e AC-7; 0245; 03-us-kad-6o].
 *
 * ⛔ **`token: none` is the server's fact, not a choice.** A successful `POST /enrolment` CONSUMES the
 * layer-1 subject and clears its cookie [`enrolment.controller.ts` → `recordSuccess`], so a device that has
 * enrolled holds no token whatever this store last said. Leaving `token: issued` would make the pad's
 * mount skip the remint and send the re-typed PIN with no subject — and [0203]'s three-strike lock would
 * count it against nothing. `submit` fetches a fresh one before six digits go. `tokenRefusal` is cleared
 * with it: a refusal of a token this device no longer holds says nothing about the next one.
 *
 * ⛔ **Not the boot's landing.** A boot 401 attempted nothing and spent nothing, so it keeps
 * {@link AT_THE_PAD} and whatever token the browser holds.
 */
const AT_THE_PAD_TOKEN_SPENT = {
  ...AT_THE_PAD,
  token: TOKEN.none,
  tokenRefusal: undefined,
} as const;

/**
 * ⭐ **Where every load starts, and where the bounded recovery returns to** [03-us-kad-6i AC-11].
 *
 * ⛔ **{@link AT_THE_PAD} is deliberately NOT retargeted at it.** Six refusal paths spread that constant to
 * mean *this device is unenrolled and a staffer may type six digits*, and a boot 401 is one of them
 * [AC-9; `0285`]. The two states are different answers to different questions.
 */
const CHECKING_ENROLMENT = { ...CLEARED_ENROLMENT, enrolment: ENROLMENT.checking } as const;

/**
 * The token request currently in flight, if any — module state rather than store state on purpose.
 *
 * ⛔ **It is not a verdict and nothing renders from it**, so it is not the device holding a decision the
 * invariant reserves for the server; it is a lock on one HTTP call.
 */
let inFlight: Promise<EnrolmentTokenOutcome> | undefined;

/**
 * The enrolment probe currently in flight — the same lock as {@link inFlight}, for the same reason [AC-7].
 * Held through {@link probeOnce} and nowhere else.
 *
 * ⭐ **A re-render must not issue a second probe.** `App` asks on mount, `StrictMode` runs that effect
 * twice in development, and the bounded recovery can fire while one is already open — and on an enrolled
 * tablet a tap can arrive while the visit's reset is still asking [03-us-kad-6o AC-2].
 */
let probeInFlight: Promise<EnrolmentProbeOutcome> | undefined;

/**
 * ⛔ **A FLOOR under the recovery, and the reason it is not optional: the estate shares one address.**
 *
 * An `online` event is not a person — a branch AP flapping delivers it to every tablet behind that IP in
 * the same instant, and each recovery that lands back on the pad also remints the layer-1 token [AC-1b], so
 * one flap is two requests per tablet from one address. Twenty tablets is a burst the coarse floor [0231]
 * counts, and what a staffer then sees is *"Too many attempts."* on a PIN that was perfectly good — a
 * lockout the tablets inflicted on themselves.
 *
 * ⭐ **It bounds the SIGNAL door only.** An explicit *Try again* is a person asking, and one press still
 * issues exactly one probe with no wait [AC-10; eval row **B6c**].
 *
 * ⚠️ **The NUMBER is pinned by a test, in figures, and that is not ceremony.** A row that derived its wait
 * from this constant would follow it anywhere: widening the floor to five minutes — a tablet that takes five
 * minutes to come back from a thirty-second blip — passed every test in the repository, because every test
 * that touched it moved with it. The boundary rows write `29_999` and `30_001` out in full.
 */
const RECOVERY_FLOOR_MS = 30_000;

/**
 * ⭐ **How long a touched pad is left alone**, and ⛔ **why this is a window rather than a latch.**
 *
 * A keystroke suppressing the recovery FOREVER is one stray key — the listener is on `window`, so a bag
 * leaning on a Chromebook is enough — and the tablet then never comes back from an outage on its own, with
 * nothing logged to say why. ⭐ **So the suppression EXPIRES.** Inside the window a staffer working the pad
 * keeps their screen; outside it, a tablet nobody is standing at can recover.
 */
const PAD_IDLE_MS = 60_000;

/**
 * A small spread on a signal's probe, and ⚠️ **it is worth less than it looks — stated so nobody relies on
 * it.** Twenty tablets answering one AP flap scatter by well under two seconds, which is nothing against a
 * thirty-second floor: ⭐ **what actually bounds this route is {@link RECOVERY_FLOOR_MS}, the token guard in
 * `issueToken`, and the probe's own exemption from the coarse floor** [`0287`]. The delay is kept because
 * it costs nothing and removes the perfectly-synchronised case; ⛔ **it is not a rate limit.**
 *
 * ⛔ **Not a timer and not a schedule**: nothing re-arms it, nothing repeats, and removing it changes no
 * outcome — only the instant twenty tablets choose.
 */
const RECOVERY_JITTER_MAX_MS = 2_000;

/*
 * ⛔ **THREE MONOTONIC INSTANTS, AND THE REASONS THEY ARE THREE.**
 *
 * ⚠️ **`performance.now()`, never `Date.now()`.** A Chromebook with a dead RTC boots with a wrong wall
 * clock, stamps one of these, and NTP then corrects BACKWARDS — which makes an elapsed time negative, reads
 * as *inside the window*, and holds a door shut for the size of the correction with nothing logged.
 * `performance.now()` is monotonic by construction and cannot do that.
 *
 * ⚠️ **Seeded below every possible reading.** `performance.now()` is page-relative, so a `0` seed would put
 * the first thirty seconds of EVERY load inside the floor — suppressing the recovery exactly when a tablet
 * that just failed its boot probe needs it.
 *
 * ⛔ **And the signal clock is NOT the probe clock.** Stamping every probe — including the boot's — would
 * make the boot's own failure veto the first `online` that follows it: AP down, tablet loads at T+2 and
 * fails fast, network returns at T+15, and the door is shut because 13 < 30. `online` is edge-triggered and
 * nothing re-arms it, so that tablet waits for a human — which is the cost this story exists to remove,
 * reintroduced by the fence built to protect it. ⭐ **The floor bounds REPEATED signals, never the first.**
 */
let lastSignalProbeAt = Number.NEGATIVE_INFINITY;

/** When the pad was last touched. Module state for the same reason {@link inFlight} is: it is not a verdict. */
let padTouchedAt = Number.NEGATIVE_INFINITY;

/**
 * When a layer-1 token was last ASKED FOR — ⛔ **stamped whether or not it arrived**, which is the half a
 * success-only guard misses.
 *
 * ⚠️ **The failing case is the one this story creates.** When the first request fails — a 503 on
 * `GET /enrolment/session`, which is the 6am branch power-cycle this story exists for — `token` stays
 * `none`, so a guard that only short-circuits on success lets every recovery cycle remount the pad and hit
 * the wire again: ~2 requests per tablet per minute, ~40/min across twenty tablets, against a **60/min
 * per-address** floor [0231]. At thirty tablets the floor refuses, the refusal leaves `token` at `none`,
 * and the next cycle asks again — self-sustaining, and read by a staffer as *"Too many attempts."* on a PIN
 * that was perfectly good.
 */
let lastTokenAt = Number.NEGATIVE_INFINITY;

/**
 * How long ago, monotonically — ⛔ **`Infinity` before anything has happened**, which is what keeps every
 * window below open on the first act of a load.
 */
function since(instant: number): number {
  return performance.now() - instant;
}

/**
 * ⭐ **One enrolment probe in flight, ever** — the single-flight lock the boot, its recovery and an enrolled
 * tablet's re-check all share [03-us-kad-6i AC-7; 03-us-kad-6o AC-1, AC-2]. A caller that arrives while
 * one is open JOINS it rather than asking again.
 *
 * ⛔ **It guards nothing else.** Whether to ask, and whether the answer still applies once it arrives, are
 * each caller's own checks, before and after this call.
 */
async function probeOnce(trigger: ProbeTrigger): Promise<EnrolmentProbeOutcome> {
  probeInFlight ??= probeEnrolment(trigger).finally(() => {
    probeInFlight = undefined;
  });
  return probeInFlight;
}

export const useEnrolmentSession = create<EnrolmentSessionState>()((set, get) => ({
  /*
   * ⭐ **The app opens on the interim boot state, not on the pad** [03-us-kad-6i AC-11; decision 0280].
   * Until this story the store initialised to {@link AT_THE_PAD}, so a tablet that reloaded showed the PIN
   * pad and then flipped — a pad flash in front of a staffer on every reload, and a staffer who typed six
   * digits the enrolment never lost.
   */
  ...CHECKING_ENROLMENT,
  token: TOKEN.none,
  tokenRefusal: undefined,

  checkEnrolment: async (trigger) => {
    /*
     * ⭐ **The interim state is the only door into a probe**, which is one guard doing three jobs: a screen
     * that already has a verdict never asks again, a suite that seeds a device past enrolment issues no
     * request at all, and the recovery below has to come back through {@link CHECKING_ENROLMENT} rather
     * than firing from wherever the app happens to be.
     */
    if (get().enrolment !== ENROLMENT.checking) return;

    const outcome = await probeOnce(trigger);

    /*
     * ⛔ **The screen may have moved while the request was open**, exactly as `submitName` records for the
     * naming call. Landing a boot verdict on a device that is no longer waiting for one would overwrite
     * whatever answered in the meantime — and the design-fidelity gate is a live instance of it, since it
     * seeds a state through `__kioskSeed` after the app has mounted and while this probe is open.
     */
    if (get().enrolment !== ENROLMENT.checking) return;

    land(set, outcome);
  },

  recheckEnrolment: async (trigger) => {
    /*
     * ⛔ **Only out of the no-answer state, and only from the pad it left the boot on.** A boot that
     * received a VERDICT is never re-probed — that would be the device asking a question the server already
     * answered — and a device that has since enrolled, been locked or gone to the naming step has left this
     * question behind. ⛔ **One probe per user act** [AC-10]: this issues exactly one, and nothing
     * schedules another.
     */
    const { probeUnanswered, enrolment } = get();
    if (!probeUnanswered || enrolment !== ENROLMENT.pin) return;

    /*
     * ⭐ **A PRESS is a person and is never refused; a SIGNAL is the environment and is bounded.**
     *
     * *Try again* is the affordance AC-10 names first, and it is the only recovery that reaches the outage
     * the recovery exists for: a server-side blip leaves the device's link up, so neither `online` nor
     * `visibilitychange` ever fires. ⛔ **A press that did nothing would be the dead end this criterion
     * forbids**, so no floor, no idle window and no jitter apply to it.
     */
    if (trigger === 'signal') {
      if (since(padTouchedAt) < PAD_IDLE_MS) return reportRecoverySuppressed('pad-touched');
      if (since(lastSignalProbeAt) < RECOVERY_FLOOR_MS) return reportRecoverySuppressed('floor');
      // ⭐ Stamped HERE and nowhere else: what the floor bounds is a REPEATED signal, and the boot probe is
      // not one. A tablet whose boot failed at T+2 must still answer the `online` that arrives at T+15.
      lastSignalProbeAt = performance.now();
    }

    set({ ...CHECKING_ENROLMENT });
    if (trigger === 'signal') await jitter();
    await get().checkEnrolment(trigger);
  },

  checkVenueServes: async (trigger) => {
    /*
     * ⭐ **Only an ENROLLED tablet asks this question.** A tablet at the pad, locked, naming or still
     * booting has no venue to lose, and ⛔ a 401 on a candidate-journey call — which clears the candidate
     * and lands the pad in one tick [`onUnauthenticated`, 0245] — reaches here already at the pad, so it
     * issues no probe of its own.
     */
    if (get().enrolment !== ENROLMENT.enrolled) return false;

    const outcome = await probeOnce(trigger);

    /*
     * ⛔ **The tablet may have left the enrolled states while the request was open** — a candidate-journey
     * 401 lands the pad with its own message [0263], and a verdict landing on top of it would wipe that.
     */
    if (get().enrolment !== ENROLMENT.enrolled) return false;

    return landVenueCheck(set, outcome, trigger);
  },

  issueToken: async (reason) => {
    /*
     * ⛔ **One token per browser while this app holds one** [03-us-kad-6i]. The pad asks on EVERY mount, and
     * since this story the pad is remounted by each recovery cycle — so without this line one `online` event
     * costs two counted requests per tablet rather than one, from twenty tablets behind a branch address at
     * the same instant. ⭐ **It is the same rule the guard below already states**, one scope wider: two
     * Redis keys for one browser is the thing neither wants.
     */
    if (get().token === TOKEN.issued) return;

    /*
     * ⛔ **AND A FLOOR UNDER THE RETRY, because the guard above only holds on SUCCESS.** A failed attempt
     * leaves `token` at `none`, so the pad's next mount asks again — see {@link lastTokenAt} for the
     * arithmetic that turns into a self-sustaining burst across a branch.
     *
     * ⭐ **It bounds the pad's MOUNT and never a submission.** [KAD-6c AC-1b] gives a staffer's six digits
     * one more attempt at a token before the pad comes back with the ruled error — *"a kiosk on branch wifi
     * at 7am is the case this is for"* — and that attempt is a person's, arriving at most once per six
     * digits typed. ⛔ Flooring it would silently disarm [0203]'s layer 1 for a staffer who is standing
     * there.
     */
    if (reason === 'mount' && since(lastTokenAt) < RECOVERY_FLOOR_MS) return;

    lastTokenAt = performance.now();

    /*
     * ⭐ **One request in flight, ever.** The screen asks on mount and `submit` asks again when it finds no
     * token, so a staffer who types six digits into a pad whose first request is still open would otherwise
     * mint a SECOND layer-1 subject — two Redis keys for one browser, and the three-strike lock counting
     * against whichever one the next attempt happens to carry.
     */
    inFlight ??= issueEnrolmentToken().finally(() => {
      inFlight = undefined;
    });

    const outcome = await inFlight;
    set({
      token: outcome === 'issued' ? TOKEN.issued : TOKEN.none,
      // ⭐ The coarse floor counts this GET too [0231; 0243], and the screen it produces must be the floor's
      // rather than a generic failure — which is what keeps the two 429s indistinguishable [`0262`].
      tokenRefusal: outcome === 'rate-limited' ? 'rate-limited' : undefined,
    });
  },

  pressDigit: (digit) => {
    const { enrolment, digits } = get();
    // Taps and keystrokes arrive at the same door, so the guard is stated once here rather than in each.
    if (enrolment !== ENROLMENT.pin) return;
    if (!/^[0-9]$/.test(digit)) return;
    if (digits.length >= PIN_LENGTH) return;

    /*
     * ⭐ **A touched pad is left alone for a while** [03-us-kad-6i AC-10]. A pad reached because the probe
     * could not be read is still eligible for the bounded recovery, and a window regaining focus must not
     * replace what a staffer is doing with a spinner. ⛔ **A WINDOW, never a latch**: the listener is on
     * `window`, so one stray key would otherwise disarm the recovery for the whole page load and the tablet
     * would never come back from an outage by itself.
     */
    padTouchedAt = performance.now();

    const entered = digits + digit;
    set({ digits: entered });

    /*
     * ⭐ **The sixth digit submits. There is no Submit button** [AC-2] — no submit affordance is drawn on
     * `01` or `02` at any viewport, and `06 · Connecting` is a separate state, so auto-submit is the only
     * reading that connects them.
     *
     * ⭐ **The pad closes HERE, synchronously, in the same tick as the sixth digit** — and that is the
     * re-entry guard as well as the screen change. Everything that could start a second submission comes
     * through this function, and the first line of it refuses outside `pin`; because nothing is awaited
     * between the two, a second entry cannot slip in. Putting the same check inside `submit` instead would
     * be a branch no caller can reach — dead code wearing a guard's name.
     */
    if (entered.length === PIN_LENGTH) {
      set({ enrolment: ENROLMENT.connecting, refusal: undefined });
      void submit(set, get, entered);
    }
  },

  pressBackspace: () => {
    const { enrolment, digits } = get();
    if (enrolment !== ENROLMENT.pin) return;
    // ⭐ A staffer CORRECTING an entry is as much a person at the pad as one making it [AC-10]. Without this
    // the idle window counts from their last DIGIT, and a signal sixty seconds later wipes what they typed.
    padTouchedAt = performance.now();
    set({ digits: digits.slice(0, -1) });
  },

  typeName: (value) => {
    const { enrolment, nameRefusal } = get();
    if (enrolment !== ENROLMENT.naming) return;

    /*
     * ⭐ **The bound is enforced HERE, where the value is held** [`0224`], the same way `pressDigit` refuses
     * a seventh digit. The control also carries `maxLength`, so a browser refuses the 41st keystroke and a
     * paste is clipped natively — this is what holds when the value arrives any other way, and it is why
     * there is no *too long* refusal to render: an over-length name never becomes a submission.
     *
     * ⛔ **Code POINTS, not code units.** `String.prototype.slice` counts UTF-16 units, so a name whose
     * surrogate pair straddles the 40th unit is stored with a LONE SURROGATE — which `JSON.stringify` sends
     * as a `\udXXX` escape and the three staff surfaces AC-6 names then render as `\uFFFD`. 0224 bounds what
     * a person would call a character, and the spread counts those.
     *
     * ⛔ **Nothing else is touched** — not the case, not the spacing, not the content. The name is stored as
     * typed [AC-6] and `asdf` is a perfectly good name [0149].
     */
    const bounded = [...value].slice(0, KIOSK_NAME_MAX_LENGTH).join('');

    /*
     * ⭐ **Editing clears `required` and NOTHING else** — only the refusal this DEVICE decided.
     *
     * `required` names no name: it says the field was empty when the action was tapped, and the first
     * keystroke makes it false. Leaving it stranded a screen claiming an empty field over a valid one, with
     * `aria-invalid` and the error ring still on.
     *
     * ⛔ **`taken` and `unreadable` are the SERVER's verdicts and they survive the edit**, which is the
     * behaviour *08* draws: the frame shows the colliding value in the field WITH the refusal under it, and
     * a message naming the refused name stays true while the staffer types a different one. Only the server
     * may overturn a server verdict [§ *The invariant*].
     */
    set({
      name: bounded,
      ...(nameRefusal?.kind === 'required' ? { nameRefusal: undefined } : {}),
    });
  },

  submitName: async () => {
    const { enrolment, name, namingInFlight } = get();
    if (enrolment !== ENROLMENT.naming) return;
    // One write in flight, ever: a second tap while the first is open would race two names onto one
    // enrolment, and the loser would be the one the staffer is looking at.
    if (namingInFlight) return;

    /*
     * ⭐ **Whitespace only, and it is the one transformation AC-6 allows** — *"stored as typed — no trimming
     * beyond whitespace, no case folding, no transformation"*. A name that is nothing but spaces is an empty
     * name, and the api would store it: its DTO takes `@Length(1, 64)` on the raw string.
     */
    const typed = name.trim();
    if (typed.length === 0) {
      set({ nameRefusal: { kind: 'required' } });
      return;
    }

    set({ namingInFlight: true, nameRefusal: undefined });
    const outcome = await nameKiosk(typed);

    /*
     * ⛔ **The screen may have moved while the request was open.** A `401` is reported from inside
     * `kioskFetch`, which returns this device to the PIN pad before `nameKiosk` resolves [0245] — so
     * writing a refusal here would drag a dead enrolment's error onto a cleared pad.
     */
    if (get().enrolment !== ENROLMENT.naming) {
      set({ namingInFlight: false });
      return;
    }

    switch (outcome.kind) {
      case 'named':
        // ⭐ The SERVER's string, not the typed one: what Ready names is what the roster will render.
        set({
          enrolment: ENROLMENT.ready,
          kioskName: outcome.name,
          nameRefusal: undefined,
          namingInFlight: false,
        });
        return;

      /*
       * ⭐ **The collision is the server's verdict and only the server's** [§ *The invariant*]. This device
       * holds no list of names and checks nothing: a client that decided would refuse a returning tablet
       * with no way to overrule it, and the name is free only when an admin deletes the record [`0251`].
       */
      case 'taken':
        set({ nameRefusal: { kind: 'taken', name: typed }, namingInFlight: false });
        return;

      default:
        set({ nameRefusal: { kind: 'unreadable' }, namingInFlight: false });
    }
  },

  startTakingCandidates: () => {
    if (get().enrolment !== ENROLMENT.ready) return;
    /*
     * ⭐ **From this tap onward the product is bilingual** [0026] and these three screens are not [`0226`].
     * Nothing else changes: the credential, the venue and the name are already the server's.
     */
    set({ enrolment: ENROLMENT.enrolled });
  },

  refuseCredential: () => {
    /*
     * ⭐ **The pad is not silent about it** [Operator ruling, 2026-09-16; 0263]. A device sent back here by
     * a refused credential shows the same inline error every other refusal uses — everything else about the
     * screen is the cleared, first-load state.
     */
    set({ ...AT_THE_PAD_TOKEN_SPENT, refusal: { kind: 'credential-refused' } });
  },
}));

type Set = (partial: Partial<EnrolmentSessionState>) => void;
type Get = () => EnrolmentSessionState;

/**
 * The whole boot state machine, in one place: one server answer in, one screen out [03-us-kad-6i AC-8].
 *
 * ⭐ **The fork is `published` and NOTHING ELSE** — the same question the connect asks, at the other entry
 * point [AC-8; eval row **B7**]. ⛔ **Nothing here reads `name`** and {@link EnrolmentOutcome} is not
 * widened to hold one: the naming step is [03-us-kad-6d]'s, built in parallel and omitted here by the
 * Operator's ruling of 2026-09-16.
 *
 * ⚠️ **So this is NOT `render()`, and the difference is one that moved under this story.** AC-8 was written
 * against a `render()` whose `'enrolled'` case sent `published: true` to `ENROLMENT.enrolled`; [KAD-6d]
 * landed first and now sends it to `ENROLMENT.naming`. ⛔ **Routing a BOOT through that would put the
 * naming step in front of a staffer on every reload of an already-named tablet** — the reload cost this
 * story exists to remove, wearing a different screen — and it would fail eval rows **B1** (*the candidate
 * journey returns*) and **B7** (*no naming step is reached*). ⭐ **The consequence is recorded rather than
 * discovered:** a tablet that enrols and reloads before a staffer names it comes back UNNAMED, with no
 * route back to naming from here — [03-us-kad-6i] § *Known gaps* **G4**, owner KAD-6d.
 *
 * ⛔ **And the three answers below are all of them.** *We cannot say* is not a verdict: it claims no
 * enrolment outcome at all [AC-10], which is why it lands on the plain pad with `refusal: undefined` and
 * ⛔ never on `locked`, ⛔ never on `enrolled`, and ⛔ never on a refusal that says somebody typed something.
 */
function land(set: Set, outcome: EnrolmentProbeOutcome): void {
  switch (outcome.kind) {
    case 'enrolled':
      /*
       * ⭐ **The venue's language BEFORE the verdict** [03-us-kad-1e AC-14; `eval-kad-1e` row **L14**].
       *
       * ⛔ **The order is the criterion, not tidiness.** The row asserts the FIRST paint — *"not
       * English-then-flip; a visible flash is a FAIL, because it is the screen a candidate is standing in
       * front of"* — and this line is the only thing that makes that true without relying on React's
       * batching: when `enrolment` becomes `enrolled` and Welcome mounts, the language it renders in is
       * already the venue's.
       *
       * It is taken on both branches of the fork, as the server sent it on both. ⛔ On the `false` branch
       * nothing renders it: the pad is English-only [`0226`], and `App` declares the enrolment copy's own
       * language over any screen but the candidate journey.
       */
      useKioskSession.getState().setVenueDefaultLanguage(outcome.defaultLanguage);
      if (outcome.published) {
        /*
         * The names ride along for display only [03-us-kid-7 O4]: the store keeps nothing across a reload, so
         * the boot is the only place an enrolled tablet can learn them again. ⛔ No route depends on them.
         */
        set({
          ...CLEARED_ENROLMENT,
          enrolment: ENROLMENT.enrolled,
          venueName: outcome.venueName,
          kioskName: outcome.kioskName,
        });
        return;
      }
      /*
       * ⭐ **`published: false` is the PLAIN pad** [03-us-kad-6e AC-1; decision 0429 R1/R3] — the same
       * landing as a boot 401 [`0285`]: no message, no refusal, ⛔ never `refuseCredential()`, and ⛔ never
       * the candidate journey [AC-5]. It is a degraded path, not a product state [0431]: a venue is
       * published as soon as it has a PIN, so the tablet hands back to the pad and a staffer types it again.
       *
       * ⛔ **`probeUnanswered` stays `false`.** This is a VERDICT, so the bounded recovery has nothing to
       * recover from — no `online` or *Try again* re-asks a question the server answered.
       *
       * ⭐ **The enrolment is KEPT** [AC-3; 0432]: nothing here calls the server. The same PIN typed again
       * renews the same row, through the api's own idempotency.
       */
      reportUnpublishedToPad('boot');
      set({ ...AT_THE_PAD });
      return;

    /*
     * ⭐ **The PLAIN pad, and ⛔ NOT `refuseCredential()`** [AC-9; decision `0285`]. `credential-refused`
     * means *a journey in progress was interrupted* and renders *"Incorrect PIN"* [`0263`]; at boot nobody
     * typed anything, so there is nothing to explain and no attempt to account for. ⛔ [`0263`] does not
     * reach this path: it is scoped to a 401 on a CANDIDATE-JOURNEY call.
     *
     * ⛔ **And the layer-1 token is NOT reset here, on either landing.** Nothing was attempted, so there is
     * nothing to account for — and claiming the browser holds no token when it does buys a remint of a
     * route that mints a Redis key per call, on every recovery cycle, from every tablet at once.
     */
    case 'unenrolled':
      set({ ...AT_THE_PAD });
      return;

    /*
     * ⛔ **No outcome is claimed, and the state is NOT terminal** [AC-10; `0262`; `0280`]. ⛔ No retry is
     * issued here: the way back is a user act — the pad's *Try again*, or the environment coming back —
     * [{@link EnrolmentSessionState.recheckEnrolment}], ⛔ never a loop and ⛔ never a timer. ⭐ **This flag
     * is what the pad renders that affordance from**, and it is the only state that carries it.
     *
     * ⏳ **WHAT THE SCREEN SAYS is not ruled** — [03-us-kad-6i] § *Known gaps* **G2**, whose two candidates
     * are the plain pad and the pad carrying `RULED_COPY.havingIssues`. ⭐ **The plain pad is what ships**,
     * because the other candidate would extend a string [`0262`] ruled for `POST /enrolment` and the
     * layer-1 token call to a new entry point by inference — which AC-10 forbids in terms. Recorded for the
     * Operator, and replacing it is one line.
     */
    default:
      set({ ...AT_THE_PAD, probeUnanswered: true });
  }
}

/**
 * One answer to {@link EnrolmentSessionState.checkVenueServes} in, one screen out [03-us-kad-6o AC-1, AC-2].
 *
 * ⭐ **`published: false` and a 401 both land the PLAIN pad** [0429 R1/R3; KAD-6i AC-9; `0285`] — no
 * message, no refusal, ⛔ never `refuseCredential()`, whose *"Incorrect PIN"* [0263] tells a staffer
 * something was typed. The only difference from the boot's landing is the layer-1 token: an enrolled
 * tablet's was spent by the connect that enrolled it [{@link AT_THE_PAD_TOKEN_SPENT}].
 *
 * ⛔ **An unreadable answer claims no outcome** [KAD-6i AC-10's posture; 0433; 0430 § 1]: the welcome stays,
 * nothing is landed, and the tap does not start the journey. ⛔ No retry is issued — the next tap is a
 * person asking, once.
 *
 * ⛔ **The venue's language is NOT re-applied on `published: true`.** [03-us-kad-1e] AC-17 puts an
 * administrator's change in force at the next LOAD, and on a tap it would overwrite the language a candidate
 * just chose on the welcome [AC-15].
 */
function landVenueCheck(
  set: Set,
  outcome: EnrolmentProbeOutcome,
  trigger: VenueCheckTrigger,
): boolean {
  switch (outcome.kind) {
    case 'enrolled':
      if (outcome.published) return true;
      reportUnpublishedToPad(trigger);
      set({ ...AT_THE_PAD_TOKEN_SPENT });
      return false;

    case 'unenrolled':
      set({ ...AT_THE_PAD_TOKEN_SPENT });
      return false;

    default:
      return false;
  }
}

/**
 * ⭐ **A suppressed recovery leaves a trace** [AC-13's discipline, at the one decision nothing else records].
 *
 * ⛔ **Both bounds above return silently otherwise**, so a tablet that declined to answer an `online` event
 * is observable to nobody — and the two reasons are not the same event: *a person is using the pad* needs
 * nobody, while *the floor held* repeated across a branch is the shape of an estate-wide outage.
 *
 * ⚠️ **`warn`, not `error`, and the level is the point.** This is a routine, expected, high-frequency
 * decision; at error level it would swamp the alert rules that consume this app's `alert:` lines [`0237`,
 * `0308`] and teach a reader to ignore them. ⛔ Nothing identifying: no `kioskId`, no venue, no PIN.
 */
function reportRecoverySuppressed(reason: 'floor' | 'pad-touched'): void {
  console.warn({
    msg: 'a boot recovery signal was not answered — the tablet is still showing the pad with no verdict',
    alert: 'boot_recovery_suppressed',
    reason,
  });
}

/**
 * ⭐ **A `published: false` landing leaves a trace** [03-us-kad-6e; 0429 R3 forbids a message on the pad,
 * not a log line].
 *
 * ⛔ **Without it the degraded path is silent on the device.** Since [0431] `published: false` reaches a
 * tablet only when something failed upstream — the auto-publish, a PIN that predates it, or a configuration
 * read the api answered as `false` [ISS-212] — and the pad it lands on looks exactly like a never-enrolled
 * tablet's. This line is how an operator tells the two apart.
 *
 * ⚠️ **`error`, and the level is the point**: unlike {@link reportRecoverySuppressed} this is not routine —
 * an ordinary venue is published the moment it has a PIN. ⛔ Nothing identifying: no `kioskId`, no venue,
 * no PIN.
 */
function reportUnpublishedToPad(trigger: 'boot' | 'connect' | VenueCheckTrigger): void {
  console.error({
    msg: 'the server answered published: false — the tablet is back at the plain pad',
    alert: 'kiosk_unpublished_to_pad',
    trigger,
  });
}

/**
 * A one-shot, randomised pause before a SIGNAL's probe — see {@link RECOVERY_JITTER_MAX_MS}.
 *
 * ⛔ **Not a retry and not a schedule.** It delays a request that a signal has already asked for, once, and
 * the screen is already showing the wait while it elapses.
 */
async function jitter(): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, Math.random() * RECOVERY_JITTER_MAX_MS));
}

/**
 * Sends the six digits, and renders whatever comes back.
 *
 * ⭐ **THE PAD IS ALREADY CLOSED WHEN THIS RUNS, and that ordering is the fix to a real defect.** It used
 * to fetch the token first and only then move to *Connecting*, so between the sixth digit and the first
 * response the pad stayed fully live for up to ten seconds, six dots filled, no spinner and nothing
 * announcing a wait. A staffer reads that as *the tablet ignored me*: they backspace and retype, the
 * second entry submits, and ⛔ **one entry has spent two of three strikes on a lock only an admin clears.**
 * `pressDigit` now moves to `connecting` in the same tick as the sixth digit, before anything is awaited —
 * ⭐ **which is the re-entry guard too**, since every path into a submission passes its `pin`-only check.
 *
 * The PIN arrives as an argument rather than being read back off the store, because by the time this runs
 * the store is already in `connecting` and `digits` is nobody's business but the screen it left.
 *
 * ⛔ **It will not submit without the layer-1 token** [AC-1b]. The three-strike device lock is keyed to that
 * token, so a PIN tried without one counts against nothing and [0203]'s layer 1 is silently absent while
 * every screen still looks right. One more attempt to obtain it — a kiosk on branch wifi at 7am is the case
 * this is for — and then the pad comes back with the ruled inline error. ⛔ **No screen is invented for it.**
 */
async function submit(set: Set, get: Get, pin: string): Promise<void> {
  if (get().token !== TOKEN.issued) {
    await get().issueToken('submission');
    if (get().token !== TOKEN.issued) {
      /*
       * ⭐ **The floor's 429 keeps the floor's copy** [`0262`]. Anything else is the answer the tablet
       * cannot read — and neither claims an enrolment outcome.
       */
      const refusal: PinRefusal =
        get().tokenRefusal === 'rate-limited' ? { kind: 'rate-limited' } : { kind: 'unreadable' };
      set({ ...AT_THE_PAD, refusal });
      return;
    }
  }

  render(set, await enrolDevice(pin));
}

/** The whole state machine after a connect, in one place: one server answer in, one screen out. */
function render(set: Set, outcome: EnrolmentOutcome): void {
  switch (outcome.kind) {
    case 'enrolled':
      /*
       * ⭐ **The PIN-in path's half of [03-us-kad-1e] AC-14** — the same one line as the boot's, at the
       * other entry point, so a tablet enrolled by six digits and a tablet that reloaded wake in the same
       * language. ⛔ **Nothing translates the screens between here and the candidate**: the enrolment
       * states are English literals [decision 0226], and the pad's own copy already tells the staffer this
       * tablet *"will take on that office's jobs, language and screening"*.
       */
      useKioskSession.getState().setVenueDefaultLanguage(outcome.defaultLanguage);
      /*
       * ⭐ **THE FORK, and it happens exactly once** [AC-7]: `published` decides it, here, after the connect
       * and before any naming step.
       *
       * ⭐ **`false` is the PLAIN pad, exactly as at boot** [03-us-kad-6e AC-7; decisions 0429 R3, 0431] — no
       * inline line, no message, nothing connect-specific, ⛔ never the naming step and ⛔ never the
       * candidate journey [AC-5]. The enrolment the server just made or renewed is kept [AC-3; 0432].
       *
       * ⛔ **And the layer-1 token is gone, so the store must say so** — the one difference from the boot's
       * landing [{@link AT_THE_PAD_TOKEN_SPENT}].
       */
      if (!outcome.published) {
        reportUnpublishedToPad('connect');
        set({ ...AT_THE_PAD_TOKEN_SPENT });
        return;
      }
      set({
        // ⭐ **`true` lands on the NAMING step, not on the candidate journey** [KAD-6d]: the staffer
        // names the tablet and taps *Start* before a candidate ever sees it.
        enrolment: ENROLMENT.naming,
        digits: '',
        refusal: undefined,
        venueName: outcome.venueName,
        // ⛔ **A connect is an ANSWER** [03-us-kad-6i AC-10]: whatever this load's boot probe failed to say,
        // the server has now said it. Cleared here rather than left to the recovery's `pin`-only guard,
        // which is three hundred lines away and is the only thing that made it harmless.
        probeUnanswered: false,
      });
      return;

    case 'device-locked':
      // ⛔ The lock is the SERVER's [AC-5]. Nothing here counted to three; the server said this word — and
      // that word is an answer, so no boot is left waiting for one [03-us-kad-6i AC-10].
      set({ enrolment: ENROLMENT.locked, digits: '', refusal: undefined, probeUnanswered: false });
      return;

    case 'incorrect-pin':
      set({
        ...AT_THE_PAD,
        refusal: { kind: 'incorrect-pin', attemptsLeft: outcome.attemptsLeft },
      });
      return;

    /*
     * ⛔ **A throttle refusal is NOT the locked screen** [AC-6; 0203]. Layer 1's screen is permanent and
     * blames the device; layer 2's wait is temporary, self-clearing and nobody's fault, and reusing the lock
     * would send a staffer to find an admin over a delay that would have ended by itself. ⭐ **What it shows
     * was ruled by the Operator on 2026-09-16**: the pad keeps its keypad and the inline error says *"Too
     * many attempts. Try again later."* — no countdown, no duration, nothing about which control refused.
     * Decision 0261.
     */
    case 'throttled':
      set({ ...AT_THE_PAD, refusal: { kind: 'throttled' } });
      return;

    /*
     * The coarse public floor's flat 429 [0231; 0243] — a different counter with a different subject. ⭐ It
     * is told apart from layer 2 by the absent `outcome` and kept apart HERE, in the model, while both
     * render the SAME sentence: which control refused is exactly the extra information the ruling's
     * *nothing disclosed* principle withholds. Decision 0262.
     */
    case 'rate-limited':
      set({ ...AT_THE_PAD, refusal: { kind: 'rate-limited' } });
      return;

    default:
      // ⛔ When the server's answer cannot be read, show NO enrolment outcome at all: do not guess locked,
      // do not guess wrong PIN, do not silently retry [§ *The invariant*, failure posture].
      set({ ...AT_THE_PAD, refusal: { kind: 'unreadable' } });
  }
}

/**
 * [decision 0245] — a `401` on any candidate-journey call returns this app to the PIN pad.
 *
 * ⭐ **The candidate's data goes BEFORE the navigation does**, and the order below is the rule rather than a
 * detail: the session holds `email`, `lastName`, `birthDay`, `birthMonth` and `ssnLast4`, and navigating
 * without clearing would leave a stranger's last-4 SSN live on an unattended lobby machine.
 *
 * ⭐ It also makes the PIN pad a CLEARED state on every entry, which is what 0245 asks for — the idle timer
 * covers `screen !== welcome`, and `endCandidateSession` leaves the flow on `welcome`.
 *
 * ⛔ **A 503 never arrives here** — `kioskFetch` reports a 401 and nothing else, because the store not
 * answering is not the same verdict as *you are not enrolled*.
 *
 * Registered at module load rather than in a component: the refusal can arrive from a fire-and-forget write
 * whose screen has already unmounted.
 */
onUnauthenticated(() => {
  useKioskSession.getState().endCandidateSession();
  useEnrolmentSession.getState().refuseCredential();
});
